import type { GridCell } from './peopleGrid';

/**
 * Owner 2026-10-27 12:37 („obsadź to obsadź”): „Obsadź” in a cell's menu needs no window. The free places of that day
 * the person may take (their functions, events they may staff — `GET …/open-day?personId`), one choice per event and
 * function: none — „Brak wolnych stanowisk w tym dniu”; one — staffed at once; several — a tiny choice under the item.
 * A day „Dostępny” is staffed („Proponowany”, the batch fill's rules); a day with no mark asks first („Wyślij
 * zapytanie”, pinned to that event and place). „Inni” is never guessed (it is filled by hand).
 */

/** A free place of `open-day` as this needs it. */
export type OpenPlace = { eventId: string; eventName: string; positionId?: string; functionId: string | null; functionName: string; label: string };
/** One event and function to staff (the first free place of it, for a question pinned to the place). */
export type Choice = { eventId: string; eventName: string; functionId: string; functionName: string; positionId?: string };
export type Cell = { personId: string; day: string };
/** A chosen cell with how it is staffed and the places it may take; `reason` (review M5) — why it has none. */
export type QuickCell = Cell & { mode: 'fill' | 'ask'; choices: Choice[]; reason?: string };

export const choiceKey = (c: Pick<Choice, 'eventId' | 'functionId'>) => `${c.eventId}:${c.functionId}`;
/** „Wesele A – Technik”. */
export const choiceLabel = (c: Pick<Choice, 'eventName' | 'functionName'>) => `${c.eventName} – ${c.functionName}`;

/**
 * A day with nothing on it (no mark, no work, no question) asks first; „Dostępny” (also „potwierdził — przydziel
 * ręcznie”) and „Z” (review M6: the question was sent already — no second one) are staffed. Among several cells (Alt) a
 * day the person works or is „Niedostępny” goes to the fill too — skipped there with its reason, never asked about.
 */
export const quickMode = (cell: GridCell | undefined): 'fill' | 'ask' =>
  !cell?.state && (cell?.assignments ?? []).length === 0 && (cell?.toAssign ?? []).length === 0 && (cell?.questions ?? []).length === 0 ? 'ask' : 'fill';

/** Review M5: free places only of „Inni” — filled by hand (undefined when there is none at all). */
export const placesReason = (places: readonly OpenPlace[]): string | undefined => (places.length > 0 && places.every((p) => p.functionId === null) ? 'wolne tylko „Inni” — obsadź ręcznie w panelu wydarzenia' : undefined);

/** Review M5: why a cell is not staffed with `choice` — its own reason, another place only, or none that day. */
export function noneReason(cell: QuickCell, choice: Pick<Choice, 'eventName' | 'functionName'>): string {
  if (cell.reason) return cell.reason;
  return cell.choices.length > 0 ? `brak wolnego miejsca „${choiceLabel(choice)}” w tym dniu` : 'brak wolnych stanowisk w tym dniu';
}

/** Review I1: one question per person — their own days only, in order. */
export function perPerson(cells: readonly Cell[]): Array<{ personId: string; days: string[] }> {
  const out = new Map<string, Set<string>>();
  for (const c of cells) out.set(c.personId, (out.get(c.personId) ?? new Set()).add(c.day));
  return [...out.entries()].map(([personId, days]) => ({ personId, days: [...days].sort() }));
}

/** The choices of one cell: one per event and function, in the order of the places; not „Inni”. */
export function cellChoices(places: readonly OpenPlace[]): Choice[] {
  const out = new Map<string, Choice>();
  for (const p of places) {
    if (p.functionId === null) continue;
    const c: Choice = { eventId: p.eventId, eventName: p.eventName, functionId: p.functionId, functionName: p.functionName, ...(p.positionId ? { positionId: p.positionId } : {}) };
    if (!out.has(choiceKey(c))) out.set(choiceKey(c), c);
  }
  return [...out.values()];
}

/** Every choice of the chosen cells, once, in their order. */
export function unionChoices(cells: readonly QuickCell[]): Choice[] {
  const out = new Map<string, Choice>();
  for (const c of cells) for (const x of c.choices) if (!out.has(choiceKey(x))) out.set(choiceKey(x), x);
  return [...out.values()];
}

/** For one choice: the cells to staff, the cells to ask, and those it does not fit (no such free place that day). */
export function splitFor(choice: Choice, cells: readonly QuickCell[]): { fill: Cell[]; ask: Cell[]; none: Cell[] } {
  const out = { fill: [] as Cell[], ask: [] as Cell[], none: [] as Cell[] };
  for (const c of cells) {
    const cell = { personId: c.personId, day: c.day };
    if (!c.choices.some((x) => choiceKey(x) === choiceKey(choice))) out.none.push(cell);
    else out[c.mode].push(cell);
  }
  return out;
}
